import React from "react";
import ReactECharts from "echarts-for-react";
import { Box, useMediaQuery } from "@mui/material";
import vasanaChart from "../../../assets/landing/svg/vasana-chart-logo.svg";
import vasanaOrgChart from "../../../assets/landing/svg/vasana-org-chart.svg";
import VSNImage from "../../../shared/VSNImage";

const MultiChart = ({
  chartType,
  data = {},
  ratio = 0,
  disabled = false,
  color = "",
  bgColor = "",
  height = "100%",
  width = "100%",
}) => {
  const isLargeScreen = useMediaQuery(
    "(min-width:1366px) and (max-width:1700px)"
  );
  const isMediumScreen = useMediaQuery(
    "(min-width:767px) and (max-width:900px)"
  );
  const vasanadata = [
    {
      type: "Sa",
      value: "Determined, Focused,<br/> Purposeful, Inventive",
      color: "#8B3F9E",
    },
    {
      type: "Re",
      value: "Energetic, Spontaneous,<br/> Friendly, Adaptable",
      color: "#1780C6",
    },
    {
      type: "Ga",
      value: "Responsible, Amenable,<br/> Supportive, Inclusive",
      color: "#FFB142",
    },
    {
      type: "Ma",
      value: "Methodical, Perfectionist,<br/> Systematic, Exacting",
      color: "#2D8F4E",
    },
  ];

  const traitMap = vasanadata.reduce((acc, { type, value }) => {
    acc[type] = value;
    return acc;
  }, {});

  const organizationData = [
    { value: 25, name: "Sa", labelValue: data?.sa },
    { value: 25, name: "Re", labelValue: data?.re },
    { value: 25, name: "Ga", labelValue: data?.ga },
    { value: 25, name: "Ma", labelValue: data?.ma },
  ];

  const pointerEvents = disabled ? "none" : "auto";

  const organizationChartOptions = {
    series: [
      {
        name: "Composition",
        type: "pie",
        radius: ["35%", "75%"],
        startAngle: 135,
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 0,
          borderColor: "#fff",
          borderWidth: 2,
        },
        label: {
          show: true,
          position: "inner",
          formatter: (params) => `${params?.data?.labelValue ?? 0}`,
          fontSize: 14,
          fontWeight: "bold",
          color: "#fff",
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 16,
            fontWeight: "bold",
          },
        },
        labelLine: {
          show: true,
        },
        data: organizationData,
        color: ["#8B3F9E", "#1780C6", "#FFB142", "#2D8F4E"],
      },
    ],
    graphic: [
      {
        type: "image",
        id: "center-image",
        left: "34.5%",
        top: "34.5%",
        style: {
          image: vasanaOrgChart,
          width: isLargeScreen ? 46 : isMediumScreen ? 46 : 62,
          height: isLargeScreen ? 46 : isMediumScreen ? 46 : 62,
          backgroundColor: "#fff",
        },
        z: 100,
        silent: true,
      },
    ],
  };

  const pieOptions = {
    series: [
      {
        name: "Progress",
        type: "pie",
        radius: ["55%", "70%"],
        label: {
          show: true,
          position: "center",
          formatter: `${ratio}%`,
          fontSize: 12,
          fontWeight: "bold",
          color: "#000",
        },
        emphasis: {
          scale: false,
          itemStyle: {
            shadowBlur: 0,
          },
        },
        labelLine: {
          show: false,
        },
        itemStyle: {
          color: (params) => {
            return params?.dataIndex === 0 ? color : bgColor;
          },
        },
        data: [ratio, 100 - ratio],
      },
    ],
  };

  const vasanaChartOptions = {
    tooltip: {
      show: true,
      position: "top",
      formatter: (params) => {
        const type = params?.name;
        const traits = traitMap[type];
        const color = vasanadata?.find((item) => item.type === type)?.color;
        return `<div style="color: ${color}; font-size: 14px;">${
          traits || ""
        }</div>`;
      },
      backgroundColor: "#fff",
      borderColor: "#ccc",
      borderWidth: 1,
      textStyle: {
        color: "#333",
      },
    },
    series: [
      {
        type: "pie",
        radius: ["40%", "92%"],
        center: ["50%", "50%"],
        startAngle: 135,
        label: {
          show: true,
          formatter: "{b}",
          position: "inside",
          fontSize: 14,
          fontWeight: "bold",
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            shadowColor: "rgba(0, 0, 0, 0.5)",
          },
        },
        data: [
          {
            value: 1,
            name: "Sa",
            selected: true,
            itemStyle: {
              color: "#F2E7F5",
              borderColor: "#8B3F9E",
              borderWidth: 1,
              emphasis: {
                color: "#8B3F9E",
                borderColor: "#8B3F9E",
                borderWidth: 1,
              },
            },
          },
          {
            value: 1,
            name: "Re",
            itemStyle: {
              color: "#E3F1F9",
              borderColor: "#1780C6",
              borderWidth: 1,
              emphasis: {
                color: "#1780C6",
                borderColor: "#1780C6",
                borderWidth: 1,
              },
            },
          },
          {
            value: 1,
            name: "Ga",
            itemStyle: {
              color: "#FFF7EC",
              borderColor: "#FFB142",
              borderWidth: 1,
              emphasis: {
                color: "#FFB142",
                borderColor: "#FFB142",
                borderWidth: 1,
              },
            },
          },
          {
            value: 1,
            name: "Ma",
            itemStyle: {
              color: "#F0F9F2",
              borderColor: "#2D8F4E",
              borderWidth: 1,
              emphasis: {
                color: "#2D8F4E",
                borderColor: "#2D8F4E",
                borderWidth: 1,
              },
            },
          },
        ],
      },
    ],
    graphic: [
      {
        type: "image",
        id: "center-image",
        left: "22%",
        top: "30%",
        style: {
          image: vasanaChart,
          width: isLargeScreen ? 82 : 100,
          height: isLargeScreen ? 89 : 107,
          backgroundColor: "red",
        },
        z: 100,
        silent: true,
      },
    ],
  };

  const getChartOptions = () => {
    switch (chartType) {
      case "VasanaCard":
        return vasanaChartOptions;
      case "Organization":
        return organizationChartOptions;
      case "Progress":
        return pieOptions;
      default:
        return {};
    }
  };

  return (
    <Box
      style={{
        pointerEvents,
        position: chartType === "Organization" ? "relative" : "",
      }}
      p={chartType === "Organization" && 2}
      mx={chartType === "Organization" && "auto"}
      display="table"
    >
      {chartType === "Organization" && (
        <>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              margin: "0 auto",
              width: "35px",
            }}
          >
            <VSNImage
              src="https://vasana-static.s3.us-east-1.amazonaws.com/images/vasana/sa/sa-large.svg"
              alt="sa logo"
            />
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: -8,
              width: "40px",
              transform: "translateY(-50%)",
            }}
          >
            <VSNImage
              src="https://vasana-static.s3.us-east-1.amazonaws.com/images/vasana/ma/ma-large.svg"
              alt="sa logo"
            />
          </Box>
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              margin: "0 auto",
              width: "35px",
            }}
          >
            <VSNImage
              src="https://vasana-static.s3.us-east-1.amazonaws.com/images/vasana/ga/ga-large.svg"
              alt="sa logo"
            />
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              right: -8,
              width: "40px",
              transform: "translateY(-50%)",
            }}
          >
            <VSNImage
              src="https://vasana-static.s3.us-east-1.amazonaws.com/images/vasana/re/re-large.svg"
              alt="sa logo"
            />
          </Box>
        </>
      )}

      <ReactECharts option={getChartOptions()} style={{ height, width }} />
    </Box>
  );
};

export default MultiChart;
